import axiosInstance from './axiosInstance';
import { tokenService } from './tokenService';

export interface ActionBarObservation {
  event_id: string;
  impression_id: string;
  kind: 'impression' | 'click';
  button_key: string;
  label: string;
}

const pending = new Map<string, ActionBarObservation[]>();
let timer: ReturnType<typeof setTimeout> | undefined;
const inFlight = new Map<string, { project_id: string; events: ActionBarObservation[] }>();
let listening = false;

/** Preserve the final click on full navigation; IDs make overlap with XHR harmless. */
export function flushActionBarOnPageHide(): void {
  const token = tokenService.getAccessToken();

  if (!token || typeof fetch === 'undefined') return;
  const batches = [...Array.from(inFlight.values()), ...Array.from(pending, ([projectId, events]) => (
    { project_id: projectId, events }
  ))];

  pending.clear();
  let remainingBytes = 60000;

  batches.forEach((batch) => {
    for (let offset = 0; offset < batch.events.length; offset += 50) {
      const body = JSON.stringify({ ...batch, events: batch.events.slice(offset, offset + 50) });
      const bytes = new globalThis.TextEncoder().encode(body).length;

      if (bytes > remainingBytes) break;
      remainingBytes -= bytes;
      void fetch(`${process.env.REACT_APP_API_URL || ''}/api/v1/tracking/action-bar-events`, {
        method: 'POST', keepalive: true, body,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      }).catch(() => undefined);
    }
  });
}

/** Bounded, best-effort telemetry never delays the action itself. */
export async function flushActionBarObservations(): Promise<void> {
  if (timer) clearTimeout(timer);
  timer = undefined;
  const batches = Array.from(pending.entries());

  pending.clear();
  // eslint-disable-next-line max-statements
  await Promise.all(batches.map(async ([projectId, events]) => {
    for (let offset = 0; offset < events.length; offset += 50) {
      const body = { project_id: projectId, events: events.slice(offset, offset + 50) };
      const key = body.events[0].event_id;

      inFlight.set(key, body);
      try {
        await axiosInstance.post('/api/v1/tracking/action-bar-events', body, { timeout: 10000 });
      } catch {
        // The endpoint deduplicates stable event IDs if an acknowledgement was lost.
        await axiosInstance.post('/api/v1/tracking/action-bar-events', body, { timeout: 10000 }).catch(() => undefined);
      } finally {
        inFlight.delete(key);
      }
    }
  }));
}

// eslint-disable-next-line max-statements
export function recordActionBarObservation(projectId: string, event: ActionBarObservation): void {
  if (!listening) {
    window.addEventListener('pagehide', flushActionBarOnPageHide);
    listening = true;
  }
  const events = pending.get(projectId) ?? [];

  if (events.length >= 200 || pending.size >= 10 && !pending.has(projectId)) return;
  events.push(event);
  pending.set(projectId, events);
  if (!timer) timer = setTimeout(() => { void flushActionBarObservations(); }, 1000);
}
